import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ChevronDown, ChevronRight, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Clause, Register, RegisterRow } from "./ContractDocumentParts";
import { VerifiedTick } from "./StatusMark";
import bankIdLogo from "../../assets/partners/bankid-logo.png";
import { AUTH_API_BASE_URL } from "../../../config/api-config";
import { getToken } from "../../../Compo/utilis/getToken";

// Prepares BankID signing in the background while the party reads the contract: the server
// fixes the exact contract documents for this version now, so pressing "Sign" only has to start
// the BankID order — fast enough for a phone to open the BankID app straight from the tap.
const preparedSigning = new Set();
function prepareSigning(contractId, role, version) {
  const key = `${contractId}:${role}:${version ?? ""}`;
  if (!contractId || !role || preparedSigning.has(key) || typeof fetch === "undefined") return;
  preparedSigning.add(key);
  const token = getToken();
  fetch(`${AUTH_API_BASE_URL}/BankId/PrepareSigning`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ contractId, role }),
  }).catch(() => preparedSigning.delete(key));
}

// The party's own statement on the page (the seller certifying the odometer, the buyer confirming
// the inspection). onChange(certified, at) is the parent's cue to save it; initialCertifiedAt
// restores a statement saved earlier. Submission handlers stay unchanged.
export function ContractOdometerAttestation({
  t,
  sellerName,
  canAct = true,
  onChange,
  id = "seller-odometer-block",
  actionId = "odometer_attest",
  statement,
  actionLabel,
  recordLabel,
  pendingText,
  showDate = true,
  // When it was already certified/confirmed and saved (a party coming back to the page).
  initialCertifiedAt = null,
}) {
  const { i18n } = useTranslation();
  const [certifiedAt, setCertifiedAt] = useState(() => (initialCertifiedAt ? new Date(initialCertifiedAt) : null));
  const formattedAt = certifiedAt && new Intl.DateTimeFormat(i18n.resolvedLanguage?.startsWith("en") ? "en-GB" : "sv-SE", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  }).format(certifiedAt);
  return (
    <div id={id} className="ds-doc-attest" data-attested={certifiedAt ? "true" : "false"}>
      <p className="ds-doc-prose ds-doc-attest-statement">{statement || t("contractUi.odometerStatement")}</p>
      {certifiedAt ? (
        <div className="ds-doc-attest-record ds-signature-status-in">
          <p className="ds-doc-attest-line" role="status">
            <VerifiedTick width={12} height={10} className="ds-signature-tick-draw" />
            <span>{recordLabel ?? t("contractUi.certifiedBy").replace("{name}", sellerName || t("role.seller"))}{showDate ? ` · ${formattedAt}` : ""}</span>
          </p>
          {canAct && <button type="button" className="ds-doc-edit" onClick={() => { setCertifiedAt(null); onChange?.(false); }}>{t("contractUi.undo")}</button>}
        </div>
      ) : canAct ? (
        <div className="ds-doc-attest-act">
          <button id={actionId} type="button" className="ds-action-outline ds-action-compact" onClick={() => { const at = new Date(); setCertifiedAt(at); onChange?.(true, at); }}>{actionLabel ?? t("contractUi.certify")}</button>
        </div>
      ) : <p className="ds-doc-attest-pending">{pendingText ?? t("contractUi.notCertified")}</p>}
    </div>
  );
}

export function ContractVehicleSourceNote({ t }) {
  // A contract modification date is not a registry retrieval date.
  return <p className="ds-doc-source">{t("contractUi.registerSource")} {t("contractUi.sellerSource")}</p>;
}

// The seller's odometer / number-of-keys editors. `value` is what is saved on the contract (the
// parent saves it from onValueChange); onEditStart / onEditCancel let the parent tell the buyer
// that this field is being changed.
export function ContractEditableVehicleNumber({ t, value, kind, onValueChange, onEditStart, onEditCancel }) {
  const { i18n } = useTranslation();
  const isOdometer = kind === "odometer";
  const initialNumber = Number.parseInt(String(value ?? "").replace(/\s/g, ""), 10);
  const [savedValue, setSavedValue] = useState(Number.isFinite(initialNumber) ? initialNumber : null);
  const [editing, setEditing] = useState(false);
  const [unit, setUnit] = useState("km");
  const [draft, setDraft] = useState(savedValue == null ? "" : String(savedValue));
  const [reason, setReason] = useState("");
  const parsed = Number.parseInt(String(draft).replace(/\s/g, ""), 10);
  const nextValue = Number.isFinite(parsed) ? (isOdometer && unit === "mil" ? parsed * 10 : parsed) : NaN;
  const valid = Number.isFinite(nextValue) && nextValue >= (isOdometer ? 1 : 1);
  const decreased = isOdometer && savedValue != null && valid && nextValue < savedValue;
  const canSave = valid && (!decreased || reason.trim().length > 0);
  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-US" : "sv-SE";
  const open = () => {
    setUnit("km");
    setDraft(savedValue == null ? "" : String(savedValue));
    setReason("");
    setEditing(true);
    onEditStart?.();
  };
  const cancel = () => {
    setEditing(false);
    setReason("");
    onEditCancel?.();
  };
  const save = (event) => {
    event?.preventDefault();
    if (!canSave) return;
    setSavedValue(nextValue);
    setEditing(false);
    setReason("");
    onValueChange?.(nextValue);
  };
  const switchUnit = (nextUnit) => {
    if (nextUnit === unit) return;
    if (valid) setDraft(String(nextUnit === "mil" ? Math.round(nextValue / 10) : nextValue));
    setUnit(nextUnit);
  };
  if (!editing) {
    return (
      <div className="ds-doc-editable-block">
        <span className={savedValue == null ? "ds-doc-value--absent" : undefined}>
          {savedValue == null ? t("agreement.notSet") : isOdometer ? `${savedValue.toLocaleString(locale)} km` : savedValue}
        </span>
        <button type="button" className="ds-doc-edit" onClick={open}>{t("contractUi.edit")}</button>
      </div>
    );
  }
  return (
    <form className="ds-doc-editing ds-doc-editor" onSubmit={save}>
      <label className="sr-only" htmlFor={`contract-${kind}-input`}>{t(isOdometer ? "buyer.odometer" : "buyer.keysCount")}</label>
      <div className={isOdometer ? "ds-doc-odometer-field" : undefined}>
        <input id={`contract-${kind}-input`} className="ds-input ds-doc-input-short" type="number" min="1" inputMode="numeric" autoFocus value={draft} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => { if (event.key === "Escape") cancel(); }} />
        {isOdometer && (
          <div className="ds-doc-unit" role="radiogroup" aria-label={t("contractUi.odometerUnit")}>
            {["mil", "km"].map((item) => <button key={item} type="button" role="radio" aria-checked={unit === item} className="ds-doc-unit-option" onClick={() => switchUnit(item)}>{item}</button>)}
          </div>
        )}
      </div>
      {isOdometer && <p className="ds-doc-row-note">{valid ? (unit === "mil" ? `${nextValue.toLocaleString(locale)} km` : `${Math.round(nextValue / 10).toLocaleString(locale)} mil`) : " "}</p>}
      {decreased && (
        <div className="ds-doc-editor-reason">
          <label className="ds-label" htmlFor="contract-odometer-reason">{t("contractUi.odometerReason")}</label>
          <input id="contract-odometer-reason" className="ds-input" value={reason} onChange={(event) => setReason(event.target.value)} placeholder={t("contractUi.odometerReasonPlaceholder")} />
          <p className="ds-doc-row-note">{t("contractUi.odometerReasonNote")}</p>
        </div>
      )}
      <div className="ds-doc-edit-actions">
        <button type="submit" className="ds-action-ink" disabled={!canSave}>{t("contractUi.save")}</button>
        <button type="button" className="ds-doc-edit" onClick={cancel}>{t("actions.cancel")}</button>
      </div>
    </form>
  );
}

// Presentation only. The owning flow retains all persistence and signing handlers.
export function ContractDialog({ open, onOpenChange, title, description, children, wide = true, referenceStyle = false }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`ds-contract-dialog ds-scope ds-form ${referenceStyle ? "ds-contract-dialog--reference" : ""} ${wide ? "max-w-2xl" : "max-w-lg"}`}
        overlayClassName="ds-contract-dialog-overlay"
        closeLabel={t("agreement.close")}
        {...(!description ? { "aria-describedby": undefined } : {})}
      >
        <DialogHeader>
          <DialogTitle className="ds-contract-dialog-title">{title}</DialogTitle>
          {description && <DialogDescription className="ds-contract-dialog-description">{description}</DialogDescription>}
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

function SourceDetails({ t, title, fields, note, contract, labels, referenceLayout }) {
  const [open, setOpen] = useState(false);
  const content = (
    <div className="ds-contract-source-details-content">
      {referenceLayout && note && <p className="ds-contract-source-note">{note}</p>}
      <dl>
        {fields.map((field) => (
          <div key={field}>
            <dt>{t(`buyer.${labels[field]}`)}</dt>
            <dd>{contract?.[`carInfo${field[0].toUpperCase()}${field.slice(1)}`] ?? t("agreement.notSet")}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
  if (referenceLayout) {
    return (
      <div className="ds-contract-source-details" data-open={open ? "true" : "false"}>
        <button type="button" className="ds-contract-source-trigger" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
          <span>{title}</span>
          <span className="ds-contract-source-summary-action">
            {t(open ? "contractUi.source.hide" : "contractUi.source.showMore")}
            <ChevronDown size={16} aria-hidden="true" />
          </span>
        </button>
        {open && content}
      </div>
    );
  }
  return (
    <details className="ds-contract-source-details">
      <summary><span>{title}</span></summary>
      {content}
    </details>
  );
}

// One register fact, exactly as the document sets label and value (matches
// the mock's VehicleSourceDetailsModal Row).
function SourceRow({ label, value }) {
  return (
    <div className="ds-source-reg-row">
      <p className="ds-source-reg-label">{label}</p>
      <p className="ds-source-reg-value">{value}</p>
    </div>
  );
}

// A ruled disclosure row — the document's rhythm, never a boxed accordion.
function SourceDisclosure({ t, id, label, open, onToggle, children }) {
  return (
    <div className="ds-source-row">
      <button type="button" className="ds-source-toggle" aria-expanded={open} aria-controls={id} onClick={onToggle}>
        <span className="ds-source-toggle-label">{label}</span>
        <span className="ds-source-toggle-more">
          {open ? t("contractUi.source.hide") : t("contractUi.source.showMore")}
          <ChevronDown size={14} className="ds-source-chevron" aria-hidden="true" />
        </span>
      </button>
      <div id={id} hidden={!open}>{children}</div>
    </div>
  );
}

// The provenance appendix: the document's own voice — headings, hairlines,
// a label/value register — matching the mock's VehicleSourceDetailsModal
// exactly (no badges, no "official source" pills: a colour that means
// "this is important" is decoration, not semantics).
function SourceAppendix({ t, open, onOpenChange, contract, technical, environment, labels, date, locale }) {
  const [technicalOpen, setTechnicalOpen] = useState(false);
  const [environmentOpen, setEnvironmentOpen] = useState(false);
  const fieldValue = (field) => contract?.[`carInfo${field[0].toUpperCase()}${field.slice(1)}`] ?? t("agreement.notSet");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="bg-black/50"
        closeLabel={t("agreement.close")}
        aria-describedby={undefined}
        className="ds-scope ds-source font-ds-sans w-full max-w-[640px] max-h-[88vh] gap-0 border-ds-rule-edge"
      >
        <div className="ds-source-head">
          <DialogTitle className="ds-source-title">{t("contractUi.source.title")}</DialogTitle>
          <DialogDescription className="ds-source-lead">{t("contractUi.source.description")}</DialogDescription>
        </div>
        <div className="ds-source-body">
          <section className="ds-source-section">
            <h3 className="ds-source-h">{t("contractUi.source.verifiedTitle")}</h3>
            {date && !Number.isNaN(date.getTime()) && (
              <p className="ds-source-meta">
                {t("contractUi.source.fetched")} {date.toLocaleString(locale, { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </p>
            )}
            <p className="ds-source-prose">{t("contractUi.source.verifiedDescription")}</p>
            <div className="ds-source-rows">
              <SourceDisclosure t={t} id="source-technical" label={t("contractUi.source.technicalData")} open={technicalOpen} onToggle={() => setTechnicalOpen((v) => !v)}>
                <div className="ds-source-register">
                  {technical.map((field) => (
                    <SourceRow key={field} label={t(`buyer.${labels[field]}`)} value={fieldValue(field)} />
                  ))}
                </div>
              </SourceDisclosure>
              <SourceDisclosure t={t} id="source-environment" label={t("contractUi.source.environmentFuel")} open={environmentOpen} onToggle={() => setEnvironmentOpen((v) => !v)}>
                <div className="ds-source-register">
                  {environment.map((field) => (
                    <SourceRow key={field} label={t(`buyer.${labels[field]}`)} value={fieldValue(field)} />
                  ))}
                  <p className="ds-source-note">{t("contractUi.source.wltpNote")}</p>
                </div>
              </SourceDisclosure>
            </div>
          </section>
          <section className="ds-source-section">
            <h3 className="ds-source-h">{t("contractUi.source.sellerInfoTitle")}</h3>
            <p className="ds-source-prose">{t("contractUi.source.sellerInfoDescription")}</p>
          </section>
          <section className="ds-source-section">
            <h3 className="ds-source-h">{t("contractUi.source.importantToKnow")}</h3>
            <p className="ds-source-prose">{t("contractUi.source.legalText")}</p>
          </section>
          <section className="ds-source-section ds-source-section--provider">
            <h3 className="ds-source-h">{t("contractUi.source.aboutBilvision")}</h3>
            <p className="ds-source-prose">{t("contractUi.source.bilvisionDescription")}</p>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ContractSourceButton({ t, contract, referenceLayout = false }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const stamp = contract?.lastModificationTime || contract?.creationTime;
  const date = stamp ? new Date(stamp) : null;
  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-US" : "sv-SE";
  const technical = ["vehicleDesignation", "yearModel", "waxelbarge", "feedingStall", "inspectionStation"];
  const environment = ["fuel", "electricVehicleConfiguration", "tax", "malus"];
  const labels = { vehicleDesignation: "vehicleDesignation", yearModel: "yearModel", waxelbarge: "transmission", feedingStall: "odometer", inspectionStation: "inspectionStation", fuel: "fuel", electricVehicleConfiguration: "electricVehicleConfiguration", tax: "tax", malus: "malus" };
  const details = (title, fields, note) => (
    <SourceDetails t={t} title={title} fields={fields} note={note} contract={contract} labels={labels} referenceLayout={referenceLayout} />
  );
  if (referenceLayout) {
    return (
      <>
        <button type="button" className="ds-doc-aside" onClick={() => setOpen(true)} aria-label={t("agreement.aboutDataAria")}>
          {t("agreement.aboutData")}
        </button>
        <SourceAppendix t={t} open={open} onOpenChange={setOpen} contract={contract} technical={technical} environment={environment} labels={labels} date={date} locale={locale} />
      </>
    );
  }
  return (
    <>
      <button type="button" className="ds-doc-aside" onClick={() => setOpen(true)} aria-label={t("agreement.aboutDataAria")}>
        {t("agreement.aboutData")}
      </button>
      <ContractDialog
        open={open}
        onOpenChange={setOpen}
        title={t("agreement.sourceModalTitle")}
        description={t("contractUi.sourceDescription")}
      >
        <div className="ds-contract-source">
          <section>
            <h3><CheckCircle2 size={20} aria-hidden="true" />{t("agreement.sourceModalVerifiedTitle")}</h3>
            <p>{t("agreement.sourceModalVerifiedBody")}</p>
            <p>{t("agreement.sourceModalUnavailableNote")}</p>
            {date && !Number.isNaN(date.getTime()) && <p className="ds-meta">{t("agreement.lastChanged")} {date.toLocaleString(i18n.resolvedLanguage?.startsWith("en") ? "en-GB" : "sv-SE")}</p>}
            {details(t("contractUi.technicalData"), technical)}
            {details(t("contractUi.environmentData"), environment)}
          </section>
          <section>
            <h3><Info size={20} aria-hidden="true" />{t("agreement.sourceModalSellerTitle")}</h3>
            <p>{t("agreement.sourceModalSellerBody")}</p>
          </section>
          <p className="ds-contract-source-legal">{t("agreement.sourceModalLegal")}</p>
          <section>
            <h3>{t("agreement.sourceModalAboutBilvisionTitle")}</h3>
            <p>{t("agreement.sourceModalAboutBilvisionBody")}</p>
          </section>
        </div>
      </ContractDialog>
    </>
  );
}

// Matches the mock's DebtModule/LoanModule exactly: a plain radio choice —
// title + the "Uppgifter från säljaren" lead, nothing else — no amount or
// bank-name input fields, and no extra description paragraph either.
export function ContractOptionRow({ t, label, name, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const selected = options.find((option) => option.value === value);
  return (
    <RegisterRow label={label} wide action={onChange && (
      <button type="button" className="ds-doc-edit" aria-label={`${t("contractUi.edit")} ${label}`} onClick={() => { setDraft(value); setOpen(true); }}>
        {t("contractUi.edit")}
      </button>
    )}>
      <span>{selected?.label || t("agreement.notSet")}</span>
      {onChange && (
        <ContractDialog open={open} onOpenChange={setOpen} title={label} description={t("contractUi.debtsLoansSource")} wide={false}>
          <fieldset className="ds-contract-options">
            <legend className="sr-only">{label}</legend>
            {options.map((option) => (
              <div key={option.value} className="ds-contract-option-group">
                <label className="ds-contract-option" data-selected={draft === option.value}>
                  <input type="radio" name={name} value={option.value} checked={draft === option.value} disabled={option.disabled} onChange={() => setDraft(option.value)} />
                  <span>{option.label}</span>
                </label>
              </div>
            ))}
          </fieldset>
          {/* Primary first, its quiet secondary beside it (matches the mock's
              debt/loan edit modals) instead of two boxed buttons pushed right. */}
          <div className="ds-contract-dialog-actions ds-contract-dialog-actions--doc">
            <button type="button" className="ds-action-ink" onClick={() => { onChange(draft); setOpen(false); }}>{t("contractUi.save")}</button>
            <button type="button" className="ds-action-text" onClick={() => setOpen(false)}>{t("agreement.close")}</button>
          </div>
        </ContractDialog>
      )}
    </RegisterRow>
  );
}

export function ContractMissingDetails({ t, kind }) {
  return (
    <Register className={kind === "condition" ? "mt-4" : undefined}>
      <RegisterRow label={t(kind === "condition" ? "buyer.knownDefects" : "contractUi.handoverDate")} wide>
        <span className="ds-doc-value--absent">{t("agreement.notSet")}</span>
      </RegisterRow>
    </Register>
  );
}

const SIGNED_AT_MONTHS_SV = ["jan", "feb", "mars", "apr", "maj", "juni", "juli", "aug", "sep", "okt", "nov", "dec"];
const SIGNED_AT_MONTHS_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function formatSignedAt(iso, english) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const months = english ? SIGNED_AT_MONTHS_EN : SIGNED_AT_MONTHS_SV;
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}, ${hh}:${mm}`;
}

// Matches the mock: clicking the sign button goes straight to BankID — no
// intermediate "confirm signing" summary screen. Once signed, nothing else
// changes on this clause but the party's own status line (and its date).
export function ContractSignatureClause({ t, role, contract, sellerName, buyerName, loading, onConfirm, requireAction = false, canConfirm = true, remaining = [], displayAllUnsigned = false, signedAt = {}, signing = false, onCancelSigning }) {
  const { i18n } = useTranslation();
  const english = i18n.resolvedLanguage?.startsWith("en");
  const sectionRef = useRef(null);
  const ownSigned = !!signedAt?.[role];
  useEffect(() => {
    if (!ownSigned && canConfirm) prepareSigning(contract?.id, role, contract?.lastModificationTime);
  }, [contract?.id, contract?.lastModificationTime, role, ownSigned, canConfirm]);
  const [inView, setInView] = useState(true);
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined" || !sectionRef.current) return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.15 });
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  // "Not yet signed" is the hard-coded default for both parties on every
  // load — signed status is never read back from the contract record here.
  // A party only ever becomes "signed" within THIS session: either this
  // party's own BankID call just succeeded, or the other party's success
  // arrived over SignalR. `signedAt` is that session's own memory of it.
  const isPartySigned = (partyRole) => !!signedAt[partyRole];
  const signed = isPartySigned(role);
  const showSigningAction = requireAction || !signed;
  const bothSigned = isPartySigned("seller") && isPartySigned("buyer");
  const otherName = role === "seller" ? buyerName : sellerName;

  // A signature that ARRIVES while this clause is mounted draws its tick
  // once; one already present on mount is history and renders finished
  // (matches the mock's `seen`/`drawn` pair).
  const sellerSigned = isPartySigned("seller");
  const buyerSigned = isPartySigned("buyer");
  const seen = useRef({ seller: sellerSigned, buyer: buyerSigned });
  const [drawn, setDrawn] = useState({ seller: false, buyer: false });
  useEffect(() => {
    if (sellerSigned && !seen.current.seller) {
      seen.current.seller = true;
      setDrawn((d) => ({ ...d, seller: true }));
    }
    if (buyerSigned && !seen.current.buyer) {
      seen.current.buyer = true;
      setDrawn((d) => ({ ...d, buyer: true }));
    }
  }, [sellerSigned, buyerSigned]);

  // While BankID is doing its work the rest of the document recedes —
  // this clause stays put, everything else quiets down.
  useEffect(() => {
    const sheet = sectionRef.current?.closest(".ds-doc");
    if (!sheet) return;
    sheet.classList.toggle("ds-doc-receded", signing);
    return () => sheet.classList.remove("ds-doc-receded");
  }, [signing]);

  const [cancelled, setCancelled] = useState(false);
  useEffect(() => {
    if (signing) setCancelled(false);
  }, [signing]);
  const handleCancelSigning = () => {
    setCancelled(true);
    onCancelSigning?.();
  };
  const handleSign = (event) => {
    setCancelled(false);
    onConfirm?.(event);
  };

  const scrollToSignature = () => {
    sectionRef.current?.scrollIntoView({ behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
    sectionRef.current?.focus({ preventScroll: true });
  };
  const scrollToRequirement = (targetId) => {
    const target = document.getElementById(targetId);
    target?.scrollIntoView?.({ behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "center" });
    target?.focus?.({ preventScroll: true });
  };
  return (
    <>
      <Clause id="section-signature" number={9} title={t("agreement.signaturesTitle")}>
        <div ref={sectionRef} tabIndex={-1} className="ds-contract-signatures">
          <p className="ds-doc-row-note">{t("contractUi.signatureLead")}</p>
          <ol className="ds-contract-signature-list" aria-label={t("agreement.signaturesTitle")}>
            {[{ role: "seller", name: sellerName }, { role: "buyer", name: buyerName }].map((party) => (
              <li key={party.role}>
                <div>
                  <p className="ds-doc-party-name">{party.name || t("agreement.notSet")}</p>
                  <p className="ds-doc-party-role">{t(`role.${party.role}`)}</p>
                </div>
                <p className={`ds-contract-signature-status${isPartySigned(party.role) && drawn[party.role] ? " ds-signature-status-in" : ""}`}>
                  {isPartySigned(party.role) && <VerifiedTick width={12} height={10} className={drawn[party.role] ? "ds-signature-tick-draw" : undefined} />}
                  {t(isPartySigned(party.role) ? "agreement.signedWith" : "agreement.notYetSigned")}
                  {isPartySigned(party.role) && formatSignedAt(signedAt[party.role], english) && (
                    <span> · {formatSignedAt(signedAt[party.role], english)}</span>
                  )}
                </p>
              </li>
            ))}
          </ol>
          {!showSigningAction ? (
            <p className="ds-doc-prose">{bothSigned ? t("contractUi.bothSigned") : t("contractUi.waitingFor", { name: otherName || t(`role.${role === "seller" ? "buyer" : "seller"}`) })}</p>
          ) : signing ? (
            // Matches the mock's busy state: the button hands over to this
            // in-place progress line while BankID does its work — no route,
            // no toast, the document just waits.
            <div className="mt-2" role="status" aria-live="polite">
              <div className="flex items-center gap-3">
                <img src={bankIdLogo} alt="" aria-hidden="true" className="ds-signature-bankid" style={{ height: 22 }} />
                <p className="text-[15px] font-semibold text-ds-ink">{t("contractUi.signatureInProgress")}</p>
              </div>
              <div className="ds-signature-progress mt-4" aria-hidden="true"><span /></div>
              {onCancelSigning && (
                <button type="button" className="ds-action-text mt-2 px-2 text-ds-muted hover:text-ds-ink" onClick={handleCancelSigning}>
                  {t("actions.cancel")}
                </button>
              )}
            </div>
          ) : (
            <>
              {remaining.length > 0 ? (
                <p id="signature-readiness" className="ds-contract-sign-remaining">
                  <span>{t("contractUi.remaining")}</span>{" "}
                  {remaining.map((item, index) => (
                    <React.Fragment key={item.targetId || item.label}>
                      {index > 0 && <span aria-hidden="true"> · </span>}
                      {item.interactive === false ? (
                        <span className="ds-contract-sign-reminder">{item.label}</span>
                      ) : (
                        <button type="button" onClick={() => scrollToRequirement(item.targetId)}>{item.label}</button>
                      )}
                    </React.Fragment>
                  ))}
                </p>
              ) : (
                <p id="signature-readiness" className="ds-doc-prose">{t(canConfirm ? "contractUi.signatureReady" : "contractUi.signLead")}</p>
              )}
              {cancelled && (
                <p className="ds-doc-row-note mt-1" role="status">{t("contractUi.signatureCancelled")}</p>
              )}
              <button type="button" onClick={handleSign} disabled={loading || !canConfirm} aria-busy={loading} aria-describedby="signature-readiness" className="ds-action-ink ds-cta ds-contract-sign-button">
                <img src={bankIdLogo} alt="" aria-hidden="true" className="ds-signature-bankid" />
                {t(`${role}.signWithBankId`)}
              </button>
            </>
          )}
        </div>
      </Clause>
      {showSigningAction && !inView && (
        <div className="ds-contract-sticky">
          <button type="button" className="ds-action-ink ds-cta" onClick={scrollToSignature}>
            {t("contractUi.toSigning")}<ChevronRight size={18} aria-hidden="true" />
          </button>
        </div>
      )}
    </>
  );
}
